import React from "react";
import { formatDate } from "./utils/formatDate";

function EmptyState({ hasFilters }) {
  return (
    <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
      <svg
        className="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
        />
      </svg>
      <h3 className="mt-2 text-sm font-semibold text-gray-900">
        No notifications found
      </h3>
      <p className="mt-1 text-sm text-gray-500">
        {hasFilters
          ? "Try adjusting your filters"
          : "No notification history available yet"}
      </p>
    </div>
  );
}

function StatusBadge({ notification }) {
  const isScheduled = notification.notificationType === "scheduled";
  if (isScheduled) {
    const status = notification.status;
    const style =
      status === "pending"
        ? "bg-yellow-100 text-yellow-800 border-yellow-200"
        : status === "sent"
        ? "bg-green-100 text-green-800 border-green-200"
        : "bg-gray-100 text-gray-800 border-gray-200";
    const label =
      status === "pending" ? "Pending" : status === "sent" ? "Sent" : "Cancelled";
    return (
      <span
        className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${style}`}>
        {label}
      </span>
    );
  }
  const unread =
    notification.status === 0 || notification.status === "unread";
  return (
    <span
      className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${
        unread
          ? "bg-yellow-100 text-yellow-800 border-yellow-200"
          : "bg-green-100 text-green-800 border-green-200"
      }`}>
      {unread ? "Unread" : "Read"}
    </span>
  );
}

function TypeBadge({ notification }) {
  const isScheduled = notification.notificationType === "scheduled";
  const style = isScheduled
    ? "bg-blue-100 text-blue-800 border-blue-200"
    : "bg-red-100 text-red-800 border-red-200";
  return (
    <span
      className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${style}`}>
      {notification.type || "N/A"}
    </span>
  );
}

export default function NotificationHistoryTable({
  notifications,
  formatDate: formatDateProp,
  emptyFiltersCount,
}) {
  const format = formatDateProp || formatDate;

  if (!notifications.length) {
    return <EmptyState hasFilters={emptyFiltersCount > 0} />;
  }

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-gray-200 min-w-0">
        <table className="min-w-full divide-y divide-gray-200 bg-white">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Date & Time
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Type
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Title
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hidden md:table-cell">
                Message
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Receiver
              </th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {notifications.map((notification) => (
              <tr
                key={notification.id}
                className="hover:bg-gray-50 transition-colors">
                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs md:text-sm font-medium text-gray-900">
                  {notification.notificationType === "scheduled"
                    ? format(notification.scheduled_at)
                    : format(notification.created_date)}
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                  <TypeBadge notification={notification} />
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs md:text-sm text-gray-900 max-w-[120px] sm:max-w-xs truncate font-medium">
                  {notification.title || "-"}
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs md:text-sm text-gray-600 max-w-[120px] md:max-w-md truncate hidden md:table-cell">
                  {notification.message || "-"}
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
                  {notification.notificationType === "scheduled" ? (
                    <span className="text-gray-500 italic">
                      Scheduled for all users
                    </span>
                  ) : notification.receiverFirstName &&
                    notification.receiverLastName ? (
                    `${notification.receiverFirstName} ${notification.receiverLastName}`
                  ) : (
                    notification.receiverEmail || "-"
                  )}
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                  <StatusBadge notification={notification} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
}) {
  if (totalPages <= 1) return null;

  const from = Math.min((currentPage - 1) * itemsPerPage + 1, totalItems);
  const to = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 bg-gray-50 rounded-lg border border-gray-200 min-w-0">
      <div className="text-xs md:text-sm font-medium text-gray-700 text-center sm:text-left">
        Showing page <span className="font-bold text-gray-900">{currentPage}</span> of{" "}
        <span className="font-bold text-gray-900">{totalPages}</span> (Total:{" "}
        <span className="font-bold text-red-600">{totalItems}</span> notifications)
      </div>
      <div className="flex gap-2 items-center justify-center flex-wrap">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm transition-colors ${
            currentPage === 1
              ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
              : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
          }`}>
          Previous
        </button>
        <span className="text-xs md:text-sm text-gray-700 px-1">
          <span className="font-bold">{from}</span> -{" "}
          <span className="font-bold">{to}</span> of{" "}
          <span className="font-bold text-red-600">{totalPages}</span>
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm transition-colors ${
            currentPage === totalPages
              ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
              : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
          }`}>
          Next
        </button>
      </div>
    </div>
  );
}
